import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { Button, Icon, JurisBadge, Logo, TypeBadge, cx, type IconName } from "./components/ui";
import { CompanySwitcher } from "./components/CompanySwitcher";
import { AuthControls } from "./components/AuthControls";
import { DemoStaffToggle } from "./components/DemoStaffToggle";
import { AppNavigationDrawer } from "./components/AppNavigationDrawer";
import { isClerkEnabled } from "./lib/clerkEnv";
import { useAccess } from "./lib/access";
import { useCompany } from "./lib/company";
import { api } from "./lib/api";
import { downloadBinaryFile } from "./lib/download";
import { caughtErrorMessage } from "./domain/errors";
import { structuredSearchWorkbookBytes, structuredSearchWorkbookFilename } from "./domain/searchExportWorkbook";
import { LegislationBrowser } from "./pages/LegislationBrowser";
import { MappingDetail } from "./pages/MappingDetail";
import { PolicyMappingDetail } from "./pages/PolicyMappingDetail";
import { Updates } from "./pages/Updates";
import { LegislationViewer } from "./pages/LegislationViewer";
import { Subscriptions } from "./pages/Subscriptions";
import { Team } from "./pages/Team";
import { PolicyReview } from "./pages/PolicyReview";
import { DocumentReviews } from "./pages/DocumentReviews";
import { PolicyEditor } from "./pages/PolicyEditor";
import { ProjectRegister, ProjectCoverage } from "./pages/ProjectCoverage";
import { EditorHome } from "./pages/EditorHome";
import { LegislationEditor } from "./pages/LegislationEditor";
import { EmailUpdates } from "./pages/EmailUpdates";
import { AlertQueue } from "./pages/AlertQueue";
import { AmendmentDiffTool } from "./pages/AmendmentDiffTool";
import { StaffAdmin } from "./pages/StaffAdmin";
import { SearchText } from "./components/SearchText";
import { ObligationSets } from "./pages/ObligationSets";

// Heavy staff-only pages that pull in large optional deps (pdfjs for import, rich editor, etc).
// Lazy loaded so normal client bundles (and first paint) do not pay the cost.
// Pages use named exports, so we re-export as default for React.lazy.
const PolicyImport = lazy(() => import("./pages/PolicyImport").then((m) => ({ default: m.PolicyImport })));
const SourceImports = lazy(() => import("./pages/SourceImports").then((m) => ({ default: m.SourceImports })));

type SectionSearchResult = FunctionReturnType<typeof api.sections.search>[number];

type NavGroup = "primary" | "workspace" | "account" | "staff";
type NavItem = {
  readonly to: string;
  readonly label: string;
  readonly shortLabel?: string;
  readonly title: string;
  readonly icon: IconName;
  readonly group: NavGroup;
  readonly priority: number;
  readonly end?: boolean;
  readonly staff?: boolean;
};

export const NAV_ITEMS: readonly NavItem[] = [
  { to: "/documents", label: "Document map", shortLabel: "Map", title: "Mapping workspace", icon: "layers", group: "primary", priority: 10, end: true },
  { to: "/browse", label: "Source register", shortLabel: "Sources", title: "Source register", icon: "search", group: "primary", priority: 20 },
  { to: "/updates", label: "Updates", title: "Mapping impact", icon: "bell", group: "primary", priority: 30, end: true },
  { to: "/projects", label: "Projects & areas", title: "Projects and business areas", icon: "shield", group: "workspace", priority: 40 },
  { to: "/document-reviews", label: "Document reviews", title: "Document reviews", icon: "bell", group: "workspace", priority: 45 },
  { to: "/subscriptions", label: "Monitoring", title: "Monitoring setup", icon: "layers", group: "workspace", priority: 50 },
  { to: "/obligation-sets", label: "Saved obligations", title: "Saved obligation sets", icon: "check", group: "workspace", priority: 55 },
  { to: "/team", label: "Team", title: "Team", icon: "users", group: "workspace", priority: 60 },
  { to: "/account/billing", label: "Plan & billing", title: "Plan and billing", icon: "shield", group: "account", priority: 65 },
  { to: "/alerts", label: "Alerts", title: "Alerts", icon: "bell", group: "staff", priority: 70, staff: true },
  { to: "/editor", label: "Editor", title: "Editor", icon: "pencil", group: "staff", priority: 80, staff: true },
  { to: "/imports", label: "Imports", title: "Imports", icon: "doc", group: "staff", priority: 90, staff: true },
  { to: "/admin", label: "Admin", title: "Admin", icon: "shield", group: "staff", priority: 100, staff: true },
  { to: "/email-updates", label: "Email updates", title: "Email updates", icon: "bell", group: "staff", priority: 110, staff: true },
];

type TitleRule = { readonly title: string; readonly match: (path: string) => boolean };

const TITLE_RULES: readonly TitleRule[] = [
  { title: "Mapping detail", match: (path) => path.startsWith("/mappings/legislation/") || path.startsWith("/mappings/document/") || path.startsWith("/mappings/policy/") },
  { title: "Legislation reader", match: (path) => path.startsWith("/legislation/") },
  { title: "Document reviews", match: (path) => path === "/document-reviews" || path.startsWith("/document-reviews/") || path.startsWith("/policy-reviews/") },
  { title: "Add mapping document", match: (path) => path.startsWith("/documents/import") || path.startsWith("/policies/import") },
  { title: "Document editor", match: (path) => path.startsWith("/documents/") || path.startsWith("/policies/") },
  { title: "Compliance project", match: (path) => path.startsWith("/projects/") },
  { title: "Amendment diff tool", match: (path) => path === "/editor/diff-tool" },
  { title: "Editor", match: (path) => path.startsWith("/editor/") },
  ...NAV_ITEMS.map((item) => ({ title: item.title, match: (path: string) => isActivePath(path, item) })),
];

export const navItemsFor = (group: NavGroup, isStaff: boolean): readonly NavItem[] =>
  NAV_ITEMS
    .filter((item) => item.group === group && (!item.staff || isStaff))
    .sort((a, b) => a.priority - b.priority);

export const isActivePath = (path: string, item: NavItem): boolean => {
  if (item.to === "/documents") {
    return path === "/documents"
      || path.startsWith("/documents/")
      || path.startsWith("/mappings/document/")
      || path.startsWith("/document-reviews/")
      || path === "/policies"
      || path.startsWith("/policies/")
      || path.startsWith("/mappings/policy/")
      || path.startsWith("/policy-reviews/");
  }
  if (item.to === "/browse") {
    return path === "/browse"
      || path.startsWith("/legislation/")
      || path.startsWith("/mappings/legislation/");
  }
  return item.end === true ? path === item.to : path === item.to || path.startsWith(item.to + "/");
};

export const canonicalDocumentPath = (path: string): string => {
  if (path === "/policies") return "/documents";
  if (path.startsWith("/policies/")) return "/documents/" + path.slice("/policies/".length);
  if (path.startsWith("/mappings/policy/")) return "/mappings/document/" + path.slice("/mappings/policy/".length);
  if (path.startsWith("/policy-reviews/")) return "/document-reviews/" + path.slice("/policy-reviews/".length);
  return path;
};

const titleFor = (path: string): string =>
  TITLE_RULES.find((rule) => rule.match(path))?.title ?? "Lawstream";

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { company, companyId, status: companyStatus, error: companyError, retry: retryCompany } = useCompany();
  const { isStaff, isInternalAdmin } = useAccess();
  const runtime = useQuery(api.runtime.settings, {});
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeNavigation = useCallback(() => setNavigationOpen(false), []);
  const openNavigationFrom = useCallback((opener: HTMLButtonElement) => {
    menuButtonRef.current = opener;
    setNavigationOpen(true);
  }, []);

  const title = titleFor(location.pathname);
  const isReader = location.pathname.startsWith("/legislation/");
  const primaryNav = useMemo(() => navItemsFor("primary", isStaff), [isStaff]);
  const mobilePrimaryNav = primaryNav;
  const mobileOverflowNav: readonly NavItem[] = [];
  const workspaceNav = useMemo(() => navItemsFor("workspace", isStaff), [isStaff]);
  const accountNav = useMemo(() => runtime?.staging ? [] : navItemsFor("account", isStaff), [isStaff, runtime?.staging]);
  const staffNav = useMemo(() => navItemsFor("staff", isStaff).filter(item => item.to !== "/admin" || isInternalAdmin), [isStaff, isInternalAdmin]);
  const secondaryActive = [...mobileOverflowNav, ...workspaceNav, ...accountNav, ...staffNav].some((item) => isActivePath(location.pathname, item));
  const staffActive = staffNav.some((item) => isActivePath(location.pathname, item));

  useEffect(() => {
    setMobileMoreOpen(false);
  }, [location.pathname]);

  if (companyStatus === "error") {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--paper)] px-6">
        <div className="w-full max-w-md rounded-xl border border-rose-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-rose-50 text-rose-700">
            <Icon name="info" size={20} />
          </div>
          <h1 className="mt-4 font-serif text-xl font-semibold text-slate-900">Company workspace could not load</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {companyError ?? "Lawstream could not prepare this company workspace."}
          </p>
          <Button className="mt-5" onClick={retryCompany}>Try again</Button>
          {isClerkEnabled && <div className="mt-5 flex flex-col items-center gap-3">
            <p className="text-sm text-slate-600">Select your staging organization, or switch accounts.</p>
            <AuthControls />
          </div>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--paper)] text-slate-800">
      <DesktopRail items={primaryNav} path={location.pathname} onOpenNavigation={openNavigationFrom} />
      <AppNavigationDrawer
        open={navigationOpen}
        onClose={closeNavigation}
        openerRef={menuButtonRef}
        routeKey={`${location.pathname}${location.search}`}
        footer={(
          <div>
            <div className="ls-tech-label text-[var(--shell-muted)]">Workspace</div>
            <div className="mt-1 text-[12.5px] font-semibold text-slate-800">{company?.name ?? "Company workspace"}</div>
            {company?.status === "trialing" && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-800"><Icon name="spark" size={13} fill /> Premium trial</div>
                <p className="mt-1 text-[11.5px] leading-snug text-slate-600">Legislative updates and document mapping.</p>
              </div>
            )}
          </div>
        )}
      >
          <NavSection label="Main" items={primaryNav} />
          <NavSection label="Workspace" items={workspaceNav} className="mt-5" />
          <NavSection label="Account" items={accountNav} className="mt-5" />
          {staffNav.length > 0 && <StaffNavSection items={staffNav} active={staffActive} className="mt-5" />}
      </AppNavigationDrawer>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-[var(--shell-line)] bg-[var(--shell-raised)] px-3 text-[var(--shell-text)] sm:gap-4 sm:px-5">
          <button
            type="button"
            aria-label="Open navigation"
            aria-expanded={navigationOpen}
            aria-controls="app-navigation-drawer"
            onClick={(event) => openNavigationFrom(event.currentTarget)}
            className="grid h-10 w-10 shrink-0 place-items-center rounded text-slate-500 hover:bg-white hover:text-slate-900 md:hidden"
          >
            <span className="grid gap-1" aria-hidden="true"><span className="h-px w-5 bg-current" /><span className="h-px w-5 bg-current" /><span className="h-px w-5 bg-current" /></span>
          </button>
          <div className="shrink-0 md:hidden"><Logo size={15} /></div>
          <div className="hidden min-w-0 md:block md:w-[11rem] xl:w-[14rem]">
            <div className="ls-tech-label text-[var(--shell-muted)]">Lawstream</div>
            <h1 className="truncate text-[13px] font-semibold leading-tight text-slate-800">{title}</h1>
          </div>
          <GlobalLawSearch companyId={companyId} />
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {isReader && (
              <div className="mr-1 hidden items-center gap-2 text-[12px] text-slate-500 lg:flex">
                <Icon name="info" size={14} /> Hover defined terms for definitions
              </div>
            )}
            <button className="relative grid h-9 w-9 place-items-center rounded text-slate-500 hover:bg-white hover:text-slate-900" aria-label={isStaff ? "Open alerts" : "Open updates"} onClick={() => navigate(isStaff ? "/alerts" : "/updates")}>
              <Icon name="bell" size={18} />
            </button>
            <div className="hidden sm:block"><DemoStaffToggle /></div>
            {isClerkEnabled ? <AuthControls /> : <CompanySwitcher />}
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden pb-14 md:pb-0">
          <Routes>
            <Route path="/" element={<Navigate to="/documents" replace />} />
            <Route path="/browse" element={<Scroll><LegislationBrowser /></Scroll>} />
            <Route path="/mappings" element={<Navigate to="/documents" replace />} />
            <Route path="/mappings/legislation/:id" element={<MappingDetail />} />
            <Route path="/mappings/document/:id" element={<PolicyMappingDetail />} />
            <Route path="/updates" element={<Scroll><Updates /></Scroll>} />
            <Route path="/legislation/:id" element={<LegislationViewer />} />
            <Route path="/document-reviews/:id" element={<Scroll><PolicyReview /></Scroll>} />
            <Route path="/document-reviews" element={<Scroll><DocumentReviews /></Scroll>} />
            <Route path="/documents" element={<PolicyMappingDetail />} />
            <Route
              path="/documents/import"
              element={
                <Suspense fallback={<div className="p-9 text-slate-500">Loading import tools...</div>}>
                  <Scroll>
                    <PolicyImport />
                  </Scroll>
                </Suspense>
              }
            />
            <Route path="/documents/:id" element={<Scroll><PolicyEditor /></Scroll>} />
            <Route path="/policies" element={<LegacyDocumentRouteRedirect />} />
            <Route path="/policies/import" element={<LegacyDocumentRouteRedirect />} />
            <Route path="/policies/:id" element={<LegacyDocumentRouteRedirect />} />
            <Route path="/mappings/policy/:id" element={<LegacyDocumentRouteRedirect />} />
            <Route path="/policy-reviews/:id" element={<LegacyDocumentRouteRedirect />} />
            <Route path="/projects" element={<Scroll><ProjectRegister /></Scroll>} />
            <Route path="/projects/:id" element={<Scroll><ProjectCoverage /></Scroll>} />
            <Route path="/subscriptions" element={<Scroll><Subscriptions /></Scroll>} />
            <Route path="/obligation-sets" element={<Scroll><ObligationSets /></Scroll>} />
            <Route path="/account/billing" element={runtime?.staging ? <Navigate to="/subscriptions" replace /> : <Scroll><Subscriptions mode="billing" /></Scroll>} />
            <Route path="/team" element={<Scroll><Team /></Scroll>} />
            <Route path="/alerts" element={isStaff ? <Scroll><AlertQueue /></Scroll> : <Navigate to="/documents" replace />} />
            <Route path="/admin" element={isInternalAdmin ? <Scroll><StaffAdmin /></Scroll> : <Navigate to="/documents" replace />} />
            <Route path="/editor" element={isStaff ? <Scroll><EditorHome /></Scroll> : <Navigate to="/documents" replace />} />
            <Route path="/editor/diff-tool" element={isStaff ? <Scroll><AmendmentDiffTool /></Scroll> : <Navigate to="/documents" replace />} />
            <Route path="/editor/:id" element={isStaff ? <Scroll><LegislationEditor /></Scroll> : <Navigate to="/documents" replace />} />
            <Route
              path="/imports"
              element={
                isStaff ? (
                  <Suspense fallback={<div className="p-9 text-slate-500">Loading importers...</div>}>
                    <Scroll>
                      <SourceImports />
                    </Scroll>
                  </Suspense>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/email-updates" element={isStaff ? <Scroll><EmailUpdates /></Scroll> : <Navigate to="/documents" replace />} />
            <Route path="*" element={<Navigate to="/documents" replace />} />
          </Routes>
        </main>
      </div>

      {/* Mobile navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex gap-1 border-t border-[var(--shell-line)] bg-[var(--shell)] px-2 py-1.5 text-[var(--shell-text)] shadow-lg md:hidden">
        {mobilePrimaryNav.map((item) => (
          <MobileNavLink key={item.to} item={item} />
        ))}
        <button
          type="button"
          aria-expanded={mobileMoreOpen}
          aria-controls="mobile-more-nav"
          onClick={() => setMobileMoreOpen((open) => !open)}
          className={cx(
            "flex min-w-[4.1rem] flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[11px] font-medium",
            mobileMoreOpen || secondaryActive
              ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]"
              : "text-slate-600 hover:bg-white hover:text-slate-900",
          )}
        >
          <Icon name="more" size={17} stroke={mobileMoreOpen || secondaryActive ? 2 : 1.75} />
          <span className="whitespace-nowrap">More</span>
        </button>
      </nav>

      {mobileMoreOpen && (
        <MobileMoreNav
          workspaceNav={workspaceNav}
          accountNav={accountNav}
          primaryNav={mobileOverflowNav}
          staffNav={staffNav}
          isStaff={isStaff}
          onClose={() => setMobileMoreOpen(false)}
        />
      )}
    </div>
  );
}

function LegacyDocumentRouteRedirect() {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: canonicalDocumentPath(location.pathname),
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

function GlobalLawSearch({ companyId }: { companyId: ReturnType<typeof useCompany>["companyId"] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const closeTimer = useRef<number | null>(null);
  const searchText = query.trim();
  const matches = useQuery(
    api.sections.search,
    searchText.length >= 2
      ? companyId !== null
        ? { query: searchText, companyId, limit: 8 }
        : { query: searchText, limit: 8 }
      : "skip",
  );
  const structuredExport = useQuery(
    api.searchExports.structured,
    searchText.length >= 2 && companyId !== null ? { query: searchText, companyId } : "skip",
  );
  const open = focused && searchText.length >= 2;

  useEffect(() => {
    setFocused(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setExportError(null);
    setActiveMatchIndex(-1);
  }, [searchText]);

  useEffect(() => {
    if (matches !== undefined && activeMatchIndex >= matches.length) setActiveMatchIndex(-1);
  }, [activeMatchIndex, matches]);

  useEffect(() => () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);

  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setFocused(false), 120);
  };

  const keepOpen = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  };

  const openMatch = (match: SectionSearchResult) => {
    setQuery("");
    setFocused(false);
    navigate(`/legislation/${match.legislation._id}?section=${match.section._id}&q=${encodeURIComponent(searchText)}`);
  };

  const openRegister = () => {
    setFocused(false);
    navigate(`/browse?search=${encodeURIComponent(searchText)}`);
  };

  const exportResults = () => {
    if (structuredExport === undefined || structuredExport.rows.length === 0) return;
    try {
      downloadBinaryFile({
        filename: structuredSearchWorkbookFilename(searchText),
        bytes: structuredSearchWorkbookBytes(structuredExport, window.location.origin),
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      setExportError(null);
    } catch (error) {
      setExportError(caughtErrorMessage(error, "Could not prepare the Excel file. Try again."));
    }
  };

  const exportLabel = companyId === null || structuredExport === undefined
    ? "Preparing Excel…"
    : structuredExport.rows.length === 0 ? "No rows to export" : "Download all results as Excel";
  const activeMatch = matches?.[activeMatchIndex];

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setFocused(false);
      setActiveMatchIndex(-1);
      return;
    }
    if (matches === undefined || matches.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setFocused(true);
      setActiveMatchIndex((current) => {
        if (event.key === "ArrowDown") return current < matches.length - 1 ? current + 1 : 0;
        return current > 0 ? current - 1 : matches.length - 1;
      });
      return;
    }
    if (event.key === "Enter" && activeMatch !== undefined) {
      event.preventDefault();
      openMatch(activeMatch);
    }
  };

  return (
    <div className="relative hidden min-w-[16rem] max-w-[38rem] flex-1 md:block" onMouseDown={keepOpen}>
      <div className="flex h-9 items-center gap-2.5 rounded border border-[var(--shell-line)] bg-white/80 px-3 focus-within:border-[var(--accent)] focus-within:ring-1 focus-within:ring-slate-300">
        <Icon name="search" size={15} className="shrink-0 text-slate-500" />
        <input
          value={query}
          role="combobox"
          aria-label="Search laws, roles or obligations"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="global-law-search-results"
          aria-activedescendant={activeMatch === undefined ? undefined : `global-law-search-option-${activeMatch.section._id}`}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={scheduleClose}
          placeholder="Search laws, roles or obligations"
          className="min-w-0 flex-1 bg-transparent text-[13px] text-slate-800 outline-none placeholder:text-slate-500"
        />
        {query !== "" && (
          <button type="button" onClick={() => setQuery("")} className="text-[11px] font-medium text-slate-500 hover:text-slate-900">Clear</button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-11 overflow-hidden rounded border border-slate-300 bg-white text-slate-800 shadow-2xl">
          {matches === undefined ? (
            <div id="global-law-search-results" role="status" aria-live="polite" className="px-4 py-4 text-[13px] text-slate-400">Searching law text...</div>
          ) : matches.length === 0 ? (
            <div id="global-law-search-results" role="status" className="px-4 py-5 text-center text-[13px] text-slate-400">No matching sections.</div>
          ) : (
            <div id="global-law-search-results" role="listbox" aria-label="Matching legislation sections" className="max-h-[27rem] overflow-y-auto divide-y divide-slate-100">
              {matches.map((match, index) => (
                <button
                  key={match.section._id}
                  id={`global-law-search-option-${match.section._id}`}
                  type="button"
                  role="option"
                  aria-selected={activeMatchIndex === index}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveMatchIndex(index)}
                  onClick={() => openMatch(match)}
                  className={cx("group flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50", activeMatchIndex === index && "bg-slate-50")}
                >
                  <div className="mt-0.5 shrink-0"><JurisBadge code={match.legislation.jurisdiction} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-[13.5px] font-semibold text-slate-900">{match.legislation.title}</span>
                      <TypeBadge type={match.legislation.kind} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-slate-400">
                      <span className="font-mono">{match.legislation.reference}</span>
                      <span className="h-1 w-1 rounded-full bg-slate-300" />
                      <span className="font-medium text-slate-500">{match.section.number} {match.section.title}</span>
                    </div>
                    {match.legislation.historical && <p className="text-xs text-amber-800">Historical snapshot</p>}
                    <p className="mt-1.5 line-clamp-2 text-[12.5px] leading-relaxed text-slate-600"><SearchText text={match.snippet} query={searchText} /></p>
                  </div>
                  <Icon name="arrowRight" size={14} className="mt-1 shrink-0 text-slate-300 group-hover:text-[var(--accent)]" />
                </button>
              ))}
            </div>
          )}
          {exportError !== null && <p className="border-t border-rose-100 bg-rose-50 px-4 py-2 text-[12px] text-rose-700" role="alert">{exportError}</p>}
          <div className="grid grid-cols-2 border-t border-slate-100 bg-slate-50/70">
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={exportResults}
              disabled={companyId === null || structuredExport === undefined || structuredExport.rows.length === 0}
              className="px-4 py-3 text-left text-[12px] font-semibold text-[var(--accent-dark)] hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
            >{exportLabel}</button>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={openRegister}
              className="flex items-center justify-between border-l border-slate-200 px-4 py-3 text-left text-[12px] font-semibold text-[var(--accent-dark)] hover:bg-slate-100"
            ><span>Open full search</span><Icon name="arrowRight" size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopRail({ items, path, onOpenNavigation }: {
  items: readonly NavItem[];
  path: string;
  onOpenNavigation: (opener: HTMLButtonElement) => void;
}) {
  return (
    <aside aria-label="Core navigation" className="hidden w-16 shrink-0 flex-col border-r border-[var(--shell-line)] bg-[var(--shell)] text-[var(--shell-text)] md:flex">
      <div className="grid h-14 shrink-0 place-items-center border-b border-[var(--shell-line)]">
        <Logo compact size={19} />
      </div>
      <nav className="flex flex-1 flex-col items-center gap-1 px-2 py-3">
        {items.map((item) => {
          const active = isActivePath(path, item);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              aria-label={item.label}
              title={item.label}
              className={cx(
                "group relative grid h-11 w-11 place-items-center rounded transition",
                active ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]" : "text-slate-600 hover:bg-white hover:text-slate-900",
              )}
            >
              {active && <span className="absolute -left-2 inset-y-2 w-0.5 bg-[var(--accent)]" aria-hidden="true" />}
              <Icon name={item.icon} size={19} stroke={active ? 2 : 1.65} />
            </NavLink>
          );
        })}
      </nav>
      <div className="grid border-t border-[var(--shell-line)] px-2 py-3">
        <button
          type="button"
          aria-label="Open all navigation"
          onClick={(event) => onOpenNavigation(event.currentTarget)}
          className="grid h-11 w-11 place-items-center rounded text-slate-600 hover:bg-white hover:text-slate-900"
        >
          <Icon name="grid" size={19} />
        </button>
      </div>
    </aside>
  );
}

function NavSection({ label, items, className = "" }: { label: string; items: readonly NavItem[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <section className={className} aria-label={label}>
      <div className="ls-tech-label mb-1.5 px-3 text-[var(--shell-muted)]">{label}</div>
      <div className="space-y-0.5">
        {items.map((item) => <SidebarNavLink key={item.to} item={item} />)}
      </div>
    </section>
  );
}

function StaffNavSection({ items, active, className = "" }: { items: readonly NavItem[]; active: boolean; className?: string }) {
  if (items.length === 0) return null;
  return (
    <section className={className} aria-label="Staff tools">
      <details className="group" open={active || undefined}>
        <summary
          className={cx(
            "flex cursor-pointer list-none items-center justify-between rounded px-3 py-2 text-sm font-semibold transition [&::-webkit-details-marker]:hidden",
            active ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]" : "text-slate-600 hover:bg-white hover:text-slate-900",
          )}
        >
          <span className="inline-flex items-center gap-2">
            <Icon name="shield" size={16} /> Staff tools
          </span>
          <Icon name="chevDown" size={15} className="transition group-open:rotate-180" />
        </summary>
        <div className="mt-1 space-y-0.5 pl-2">
          {items.map((item) => <SidebarNavLink key={item.to} item={item} compact />)}
        </div>
      </details>
    </section>
  );
}

function SidebarNavLink({ item, compact = false }: { item: NavItem; compact?: boolean }) {
  const location = useLocation();
  const active = isActivePath(location.pathname, item);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={cx(
        "group relative flex w-full items-center gap-3 rounded px-3 py-2 text-sm font-medium transition",
        compact && "text-[13px]",
        active ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]" : "text-slate-600 hover:bg-white hover:text-slate-900",
      )}
    >
      {active && <span className="absolute bottom-1.5 left-0 top-1.5 w-0.5 bg-[var(--accent)]" />}
      <Icon name={item.icon} size={compact ? 16 : 18} stroke={active ? 2 : 1.75} />
      {item.label}
    </NavLink>
  );
}

function MobileNavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const active = isActivePath(location.pathname, item);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={cx(
        "flex min-w-[4.1rem] flex-1 flex-col items-center gap-0.5 rounded px-2 py-1.5 text-[11px] font-medium",
        active ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]" : "text-slate-600 hover:bg-white hover:text-slate-900",
      )}
    >
      <Icon name={item.icon} size={17} stroke={active ? 2 : 1.75} />
      <span className="whitespace-nowrap">{item.shortLabel ?? item.label}</span>
    </NavLink>
  );
}

function MobileMoreNav({
  primaryNav,
  workspaceNav,
  accountNav,
  staffNav,
  isStaff,
  onClose,
}: {
  primaryNav: readonly NavItem[];
  workspaceNav: readonly NavItem[];
  accountNav: readonly NavItem[];
  staffNav: readonly NavItem[];
  isStaff: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <button type="button" className="absolute inset-0 bg-slate-900/20" aria-label="Close navigation" onClick={onClose} />
      <div id="mobile-more-nav" className="absolute bottom-16 left-2 right-2 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <div className="grid gap-4 p-4">
          <MobileMoreGroup label="Workspace" items={workspaceNav} onClose={onClose} />
          <MobileMoreGroup label="Account" items={accountNav} onClose={onClose} />
          {isStaff && <MobileMoreGroup label="Staff tools" items={staffNav} onClose={onClose} />}
        </div>
          <MobileMoreGroup label="More" items={primaryNav} onClose={onClose} />
      </div>
    </div>
  );
}

function MobileMoreGroup({ label, items, onClose }: { label: string; items: readonly NavItem[]; onClose: () => void }) {
  if (items.length === 0) return null;
  return (
    <section aria-label={label}>
      <div className="mb-2 text-[12px] font-semibold text-slate-500">{label}</div>
      <div className="grid gap-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClose}
            className={({ isActive }) =>
              cx(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium",
                isActive
                  ? "bg-[var(--accent-soft)] text-[var(--accent-dark)]"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon name={item.icon} size={18} stroke={isActive ? 2 : 1.75} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </section>
  );
}

/** Scrollable page wrapper. The reader manages its own internal scrolling. */
function Scroll({ children }: { children: React.ReactNode }) {
  return <div data-page-scroll className="h-full overflow-y-auto">{children}</div>;
}
